// @ts-nocheck
/**
 * Coerce a flat list or list-of-lists into chart X/Y series (DEC-078).
 * Data only — no header row (unlike DEC-077 tables).
 */

import type { ChartYAxisSide, LayoutChartType } from '../../types/component';
import { MAX_CHART_SERIES, XY_MULTI_SERIES_CHART_TYPES } from './chartTypeMigration';

export type ListChartMode =
  | 'flat'
  | 'matrix1'
  | 'xy'
  | 'xy_drop'
  | 'pairs'
  | 'empty'
  | 'invalid';

export type ListChartAuthoredSeries = {
  id: string;
  yAxis?: ChartYAxisSide;
};

export type ListChartCoercedSeries = {
  id: string;
  xList: Array<string | number | null>;
  yList: Array<string | number | null>;
  yAxis: ChartYAxisSide;
};

export type ListChartCoercionIssue = {
  severity: 'warning';
  code: 'schema.invalid_field_type';
  message: string;
};

export type ListChartCoercionResult = {
  mode: ListChartMode;
  series: ListChartCoercedSeries[];
  issues: ListChartCoercionIssue[];
};

function cellValue(value: unknown): string | number | null {
  if (value == null) return null;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'boolean') return value ? 1 : 0;
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function indexX(length: number): number[] {
  return Array.from({ length }, (_, i) => i + 1);
}

function toMatrix(value: unknown[]): unknown[][] {
  if (value.length === 0) return [];
  const hasNested = value.some((el) => Array.isArray(el));
  if (!hasNested) {
    return value.map((v) => [v]);
  }
  return value.map((row) => (Array.isArray(row) ? row : [row]));
}

function columnValues(matrix: unknown[][], colIndex: number): Array<string | number | null> {
  return matrix.map((row) => cellValue(row[colIndex]));
}

function matrixWidth(matrix: unknown[][]): number {
  let w = 0;
  for (const row of matrix) {
    if (row.length > w) w = row.length;
  }
  return w;
}

function isXyChart(chartType: LayoutChartType): boolean {
  return XY_MULTI_SERIES_CHART_TYPES.has(chartType);
}

function seriesId(authored: ListChartAuthoredSeries | undefined, index: number): string {
  if (authored?.id && authored.id.trim()) return authored.id;
  return index === 0 ? 'y' : `s${index}`;
}

function seriesAxis(
  authored: ListChartAuthoredSeries | undefined,
  allowSecondary: boolean,
): ChartYAxisSide {
  if (allowSecondary && authored?.yAxis === 'secondary') return 'secondary';
  return 'primary';
}

function padOrTrimSeries(
  built: Array<{ xList: Array<string | number | null>; yList: Array<string | number | null> }>,
  authored: ListChartAuthoredSeries[],
  allowSecondary: boolean,
): ListChartCoercedSeries[] {
  const targetCount = Math.min(
    MAX_CHART_SERIES,
    Math.max(built.length, authored.length > 0 ? authored.length : 1),
  );
  const out: ListChartCoercedSeries[] = [];
  for (let i = 0; i < targetCount; i += 1) {
    const data = built[i];
    const auth = authored[i];
    out.push({
      id: seriesId(auth, i),
      xList: data?.xList ?? [],
      yList: data?.yList ?? [],
      yAxis: seriesAxis(auth, allowSecondary),
    });
  }
  return out;
}

/**
 * Interpret `value` as chart data for the given chartType and authored series slots.
 */
export function seriesFromListValue(
  value: unknown,
  chartType: LayoutChartType,
  authoredSeries: ListChartAuthoredSeries[] = [],
): ListChartCoercionResult {
  if (!Array.isArray(value)) {
    return {
      mode: 'invalid',
      series: padOrTrimSeries([], authoredSeries, isXyChart(chartType)),
      issues: [
        {
          severity: 'warning',
          code: 'schema.invalid_field_type',
          message: 'List-backed chart source must be an array (flat list or list-of-lists).',
        },
      ],
    };
  }

  if (value.length === 0) {
    return {
      mode: 'empty',
      series: padOrTrimSeries([], authoredSeries, isXyChart(chartType)),
      issues: [],
    };
  }

  const wasFlat = !value.some((el) => Array.isArray(el));
  const matrix = toMatrix(value);
  const width = matrixWidth(matrix);
  const allowSecondary = isXyChart(chartType);
  const authored = authoredSeries.slice(0, MAX_CHART_SERIES);

  if (!isXyChart(chartType)) {
    // Non-XY: first column / flat list only.
    const yList = columnValues(matrix, 0);
    return {
      mode: wasFlat || width <= 1 ? 'flat' : width === 1 ? 'matrix1' : 'xy_drop',
      series: padOrTrimSeries([{ xList: [], yList }], authored, false),
      issues: [],
    };
  }

  if (width <= 1) {
    const yList = columnValues(matrix, 0);
    const xList = indexX(yList.length);
    return {
      mode: wasFlat ? 'flat' : 'matrix1',
      series: padOrTrimSeries([{ xList, yList }], authored, allowSecondary),
      issues: [],
    };
  }

  if (width === 2 || width === 3) {
    const xList = columnValues(matrix, 0);
    const yList = columnValues(matrix, 1);
    return {
      mode: width === 3 ? 'xy_drop' : 'xy',
      series: padOrTrimSeries([{ xList, yList }], authored, allowSecondary),
      issues: [],
    };
  }

  // width >= 4: pairs (c0,c1), (c2,c3), …; drop trailing odd column.
  const pairCount = Math.min(Math.floor(width / 2), MAX_CHART_SERIES);
  const built: Array<{
    xList: Array<string | number | null>;
    yList: Array<string | number | null>;
  }> = [];
  for (let p = 0; p < pairCount; p += 1) {
    built.push({
      xList: columnValues(matrix, p * 2),
      yList: columnValues(matrix, p * 2 + 1),
    });
  }
  return {
    mode: 'pairs',
    series: padOrTrimSeries(built, authored, allowSecondary),
    issues: [],
  };
}
